import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  List,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompletedIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  HourglassEmpty as ActiveIcon,
  Person as DriverIcon,
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  LocalTaxi as RideIcon,
  PauseCircle as BreakIcon,
  AttachMoney as EarningsIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { 
  fetchShiftById, 
  deleteShift, 
  updateShift,
  addBreakToShift
} from '../../redux/slices/shiftsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

// Styled Components
const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'completed':
      color = 'success';
      icon = <CompletedIcon />;
      break;
    case 'active':
      color = 'primary';
      icon = <ActiveIcon />;
      break;
    case 'scheduled':
      color = 'info';
      icon = <ScheduledIcon />;
      break;
    case 'cancelled':
      color = 'error';
      icon = <CancelledIcon />;
      break;
    default:
      color = 'default';
      icon = <ScheduledIcon />;
      break;
  }
  
  return {
    color: color === 'default' ? 'inherit' : 'white',
    backgroundColor: color === 'default' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ShiftDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentShift, isLoading, error } = useSelector(state => state.shifts);
  
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [breakData, setBreakData] = useState({
    start_time: '',
    end_time: '',
    reason: ''
  });
  
  useEffect(() => {
    if (id) {
      dispatch(fetchShiftById(id));
    }
  }, [dispatch, id]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    dispatch(deleteShift(id)).then(() => {
      navigate('/schichten');
    });
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleStatusChange = (newStatus) => {
    dispatch(updateShift({
      id,
      status: newStatus
    }));
  };
  
  const handleBreakDialogOpen = () => {
    setBreakDialogOpen(true);
  };
  
  const handleBreakDialogClose = () => {
    setBreakDialogOpen(false);
    setBreakData({
      start_time: '',
      end_time: '',
      reason: ''
    });
  };
  
  const handleBreakInputChange = (e) => {
    const { name, value } = e.target;
    setBreakData({
      ...breakData,
      [name]: value
    });
  };
  
  const handleBreakSubmit = () => {
    // Format the times for submission
    const shiftDate = new Date(currentShift.start_time);
    const [startHours, startMinutes] = breakData.start_time.split(':').map(Number);
    const [endHours, endMinutes] = breakData.end_time.split(':').map(Number);
    
    const startDate = new Date(shiftDate);
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    const endDate = new Date(shiftDate);
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    dispatch(addBreakToShift({
      shiftId: id,
      breakData: {
        start_time: startDate.toISOString(),
        end_time: endDate.toISOString(),
        reason: breakData.reason
      }
    }));
    
    handleBreakDialogClose();
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Nicht angegeben';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHrs} Std. ${diffMins} Min.`;
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Nicht angegeben';
    return `${parseFloat(value).toFixed(2)} €`;
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'scheduled': 'Geplant',
      'active': 'Aktiv',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert'
    };
    
    return statusMap[status] || status;
  };
  
  const canActivateShift = () => {
    return currentShift && currentShift.status === 'scheduled';
  };
  
  const canCompleteShift = () => {
    return currentShift && currentShift.status === 'active';
  };
  
  const canCancelShift = () => {
    return currentShift && ['scheduled', 'active'].includes(currentShift.status);
  };
  
  const canAddBreak = () => {
    return currentShift && ['active', 'completed'].includes(currentShift.status);
  };
  
  if (isLoading) {
    return <Loading message="Schicht wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Schicht"
        message={error}
        onRetry={() => dispatch(fetchShiftById(id))}
      />
    );
  }
  
  if (!currentShift) {
    return (
      <ErrorMessage
        title="Schicht nicht gefunden"
        message="Die angeforderte Schicht konnte nicht gefunden werden."
        onRetry={() => navigate('/schichten')}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Schichtdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichten', path: '/schichten' },
          { label: `Schicht ${formatDate(currentShift.start_time)}`, path: `/schichten/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/schichten/${id}/bearbeiten`
        }}
      />
      
      {/* Schicht Übersicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
                  Schicht am {formatDate(currentShift.start_time)}
                </Typography>
                <StatusChip 
                  label={translateStatus(currentShift.status)}
                  status={currentShift.status}
                  size="medium"
                  icon={
                    currentShift.status === 'completed' ? <CompletedIcon /> : 
                    currentShift.status === 'active' ? <ActiveIcon /> : 
                    currentShift.status === 'scheduled' ? <ScheduledIcon /> :
                    <CancelledIcon />
                  }
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Zeit:</strong> {formatTime(currentShift.start_time)} - {formatTime(currentShift.end_time)}
                  {' '}({formatDuration(currentShift.start_time, currentShift.end_time)})
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DriverIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Fahrer:</strong> {currentShift.driver?.user?.name || 'Kein Fahrer zugewiesen'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  <strong>Fahrzeug:</strong> {currentShift.vehicle ? `${currentShift.vehicle.model} (${currentShift.vehicle.license_plate})` : 'Kein Fahrzeug zugewiesen'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
              {canActivateShift() && (
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<ActiveIcon />}
                  onClick={() => handleStatusChange('active')}
                >
                  Schicht starten
                </Button>
              )}
              
              {canCompleteShift() && (
                <Button 
                  variant="contained" 
                  color="success"
                  startIcon={<CompletedIcon />}
                  onClick={() => handleStatusChange('completed')}
                >
                  Schicht abschließen
                </Button>
              )}
              
              {canCancelShift() && (
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<CancelledIcon />}
                  onClick={() => handleStatusChange('cancelled')}
                >
                  Stornieren
                </Button>
              )}
              
              {canAddBreak() && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  startIcon={<BreakIcon />}
                  onClick={handleBreakDialogOpen}
                >
                  Pause hinzufügen
                </Button>
              )}
              
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Löschen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* KPIs/Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DriverIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrer</Typography>
              </Box>
              <Typography variant="body1">{currentShift.driver?.user?.name || 'Nicht zugewiesen'}</Typography>
              {currentShift.driver && (
                <>
                  <Typography variant="body2">
                    Telefon: {currentShift.driver.user?.phone || 'Nicht angegeben'}
                  </Typography>
                  <Typography variant="body2">
                    Bewertung: {currentShift.driver.rating || '0'}/5
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      component={Link}
                      to={`/fahrer/${currentShift.driver_id}`}
                    >
                      Fahrer anzeigen
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrzeug</Typography>
              </Box>
              <Typography variant="body1">{currentShift.vehicle?.model || 'Nicht zugewiesen'}</Typography>
              {currentShift.vehicle && (
                <>
                  <Typography variant="body2">
                    Kennzeichen: {currentShift.vehicle.license_plate || 'Nicht angegeben'}
                  </Typography>
                  <Typography variant="body2">
                    Status: {translateStatus(currentShift.vehicle.status || 'inactive')}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      component={Link}
                      to={`/fahrzeuge/${currentShift.vehicle_id}`}
                    >
                      Fahrzeug anzeigen
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RideIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrten</Typography>
              </Box>
              <Typography variant="h4">{currentShift.total_rides || 0}</Typography>
              <Typography variant="body2" color="textSecondary">
                Gesamtanzahl der Fahrten während dieser Schicht
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EarningsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Einnahmen</Typography>
              </Box>
              <Typography variant="h4">{formatCurrency(currentShift.total_earnings)}</Typography>
              <Typography variant="body2" color="textSecondary">
                Gesamteinnahmen während dieser Schicht
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
      </Grid>
      
      {/* Tabs für Details */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Pausen" />
          <Tab label="Notizen" />
        </Tabs>
        
        {/* Pausen Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Pausenzeiten
            </Typography>
            {canAddBreak() && (
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleBreakDialogOpen}
              >
                Pause hinzufügen
              </Button>
            )}
          </Box>
          
          <List>
            {currentShift.break_times && currentShift.break_times.length > 0 ? (
              currentShift.break_times.map((breakTime, index) => (
                <Paper key={breakTime.id || index} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      Pause {index + 1}
                    </Typography>
                    <Chip
                      label={formatDuration(breakTime.start_time, breakTime.end_time)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Zeit:</strong> {formatTime(breakTime.start_time)} - {formatTime(breakTime.end_time)}
                    </Typography>
                    {breakTime.reason && (
                      <Typography variant="body2">
                        <strong>Grund:</strong> {breakTime.reason}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Pausen für diese Schicht eingetragen.
              </Typography>
            )}
          </List>
        </TabPanel>
        
        {/* Notizen Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Notizen
          </Typography>
          
          {currentShift.notes ? (
            <Typography variant="body1">
              {currentShift.notes}
            </Typography>
          ) : (
            <Typography variant="body1" align="center" sx={{ py: 3 }}>
              Keine Notizen für diese Schicht hinterlegt.
            </Typography>
          )}
        </TabPanel>
      </Paper>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Schicht löschen"
        content={`Möchten Sie die Schicht am ${formatDate(currentShift.start_time)} für ${currentShift.driver?.user?.name || 'unbekannt'} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      
      {/* Pause hinzufügen Dialog */}
      <Dialog open={breakDialogOpen} onClose={handleBreakDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Pause hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Startzeit"
                  name="start_time"
                  type="time"
                  value={breakData.start_time}
                  onChange={handleBreakInputChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Endzeit"
                  name="end_time"
                  type="time"
                  value={breakData.end_time}
                  onChange={handleBreakInputChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              margin="normal"
              label="Grund (optional)"
              name="reason"
              multiline
              rows={3}
              value={breakData.reason}
              onChange={handleBreakInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBreakDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleBreakSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!breakData.start_time || !breakData.end_time}
          >
            Pause hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftDetails;